// §6 Choose an account (step 1) + the two pop-ups (§6.1).
// Cards behave like radio buttons; Continue disabled until one is picked.
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Modal } from 'react-bootstrap';
import { PRODUCTS, type ProductConfig } from '@/api/constants';
import { useOnboarding } from '@/context/OnboardingContext';
import type { ProductType } from '@/types/onboarding.types';
import './onboarding.css';

const ChooseAccount = () => {
  const navigate = useNavigate();
  const { draft, setProduct, setConsentsAccepted } = useOnboarding();
  const [selected, setSelected] = useState<ProductType | null>(draft.product);
  const [readMore, setReadMore] = useState<ProductConfig | null>(null);
  const [popup, setPopup] = useState<0 | 1 | 2>(0); // 0 = none, 1 = docs, 2 = consent
  const [consentTicked, setConsentTicked] = useState(false);
  const [docView, setDocView] = useState<'terms' | 'privacy' | null>(null);

  const handleContinue = () => {
    if (!selected) return;
    setProduct(selected);
    setConsentTicked(false);
    setPopup(1);
  };

  const handleGetStarted = () => {
    setConsentsAccepted();
    setPopup(0);
    navigate('/open-account/personal-details');
  };

  return (
    <div className="nova-ob-page nova-ob-page-full">
      <div className="nova-ob-navbar">
        <div className="brand">
          <span className="mark">N</span>
          <span>NovaBank</span>
        </div>
        <span className="text-muted" style={{ fontSize: '0.85rem' }}>Account opening</span>
      </div>

      <div className="nova-ob-header">
        <button type="button" className="nova-ob-back" aria-label="Back" onClick={() => navigate('/welcome')}>
          ←
        </button>
        <h1>Open your NovaBank account in minutes</h1>
        <p>No branch visit, no paperwork — pick what you need and we will set it up as you go.</p>
      </div>

      <div className="nova-ob-body">
        <div className="nova-ob-panel">
          <h2 className="nova-ob-heading">Which account would you like?</h2>

          <div className="nova-ob-product-grid">
            {PRODUCTS.map((product) => (
              <button
                key={product.type}
                type="button"
                className={`nova-ob-product ${selected === product.type ? 'selected' : ''}`}
                onClick={() => setSelected(product.type)}
                aria-pressed={selected === product.type}
              >
                <div className="nova-ob-product-photo" aria-hidden="true">🏦</div>
                <h3>{product.name}</h3>
                <p className="desc">{product.description}</p>
                {product.bullets.map((b) => (
                  <div key={b} className="nova-ob-benefit">
                    <span className="tick">✓</span>
                    <span>{b}</span>
                  </div>
                ))}
                <p className="nova-ob-fees">{product.fees}</p>
                <span
                  className="nova-ob-readmore"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReadMore(product);
                  }}
                >
                  Read more
                </span>
              </button>
            ))}
          </div>

          <div className="nova-ob-footer">
            <div className="nova-ob-footer-inner">
              <button
                type="button"
                className="btn btn-primary btn-lg fw-semibold nova-btn"
                disabled={!selected}
                onClick={handleContinue}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Read more bottom sheet */}
      <Modal show={readMore !== null} onHide={() => setReadMore(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{readMore?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {readMore && (
            <>
              <h6>What's included</h6>
              {[...readMore.bullets, ...readMore.readMoreBullets].map((b) => (
                <div key={b} className="nova-ob-benefit">
                  <span className="tick">✓</span>
                  <span>{b}</span>
                </div>
              ))}
              <h6 className="mt-3">Fees</h6>
              <p>{readMore.fees}</p>
              <h6>Who can apply</h6>
              {readMore.whoCanApply.map((w) => (
                <div key={w} className="nova-ob-benefit">
                  <span className="tick">✓</span>
                  <span>{w}</span>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Pop-up 1 — what you'll need */}
      <Modal show={popup === 1} onHide={() => setPopup(0)} centered>
        <Modal.Body className="text-center p-4">
          <div className="nova-ob-dots">
            <span className="nova-ob-dot active" style={{ background: 'var(--nova-blue)' }} />
            <span className="nova-ob-dot" />
          </div>
          <h5 className="fw-bold">You will need the following before you begin</h5>
          <div className="text-start mt-3">
            {['Aadhaar card', 'PAN card', 'A camera on this device for a selfie'].map((item) => (
              <div key={item} className="nova-ob-benefit mb-2">
                <span aria-hidden="true">📄</span>
                <span className="fw-semibold">{item}</span>
              </div>
            ))}
          </div>
          <button type="button" className="btn btn-primary w-100 mt-3 nova-btn" onClick={() => setPopup(2)}>
            Next
          </button>
        </Modal.Body>
      </Modal>

      {/* Pop-up 2 — consent */}
      <Modal show={popup === 2} onHide={() => setPopup(0)} centered>
        <Modal.Body className="p-4">
          <div className="nova-ob-dots justify-content-center">
            <span className="nova-ob-dot" />
            <span className="nova-ob-dot active" style={{ background: 'var(--nova-blue)' }} />
          </div>
          <h5 className="fw-bold text-center">Let's get started</h5>
          <p className="text-center">You're just minutes away from opening your account.</p>

          <div className="form-check text-start">
            <input
              id="consent"
              type="checkbox"
              className="form-check-input"
              checked={consentTicked}
              onChange={(e) => setConsentTicked(e.target.checked)}
            />
            <label htmlFor="consent" className="form-check-label" style={{ fontSize: '0.85rem' }}>
              I confirm my details are correct and I have read and understood the NovaBank{' '}
              <button type="button" className="btn btn-link p-0 align-baseline fw-bold" style={{ fontSize: '0.85rem' }} onClick={() => setDocView('terms')}>
                Terms and Conditions
              </button>
              , and I have read and understood the NovaBank{' '}
              <button type="button" className="btn btn-link p-0 align-baseline fw-bold" style={{ fontSize: '0.85rem' }} onClick={() => setDocView('privacy')}>
                Privacy Notice
              </button>{' '}
              and consent to the use of my personal data as stipulated therein.
            </label>
          </div>

          <div className="d-flex gap-2 mt-4">
            <button type="button" className="btn btn-outline-primary w-100" onClick={() => setPopup(0)}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary w-100 nova-btn"
              disabled={!consentTicked}
              onClick={handleGetStarted}
            >
              Get Started
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Terms / Privacy document view */}
      <Modal show={docView !== null} onHide={() => setDocView(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{docView === 'terms' ? 'Terms and Conditions' : 'Privacy Notice'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The full {docView === 'terms' ? 'Terms and Conditions' : 'Privacy Notice'} will be
          published here before the app goes live.
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChooseAccount;
